import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdmin } from '../contexts/AdminContext';
import { useApp } from '../contexts/AppContext';
import { getQuoteCategories, getAllTopics, getQuotesByTopic, createQuote, updateQuote, deleteQuote, createCategory, createTopic } from '../lib/quoteService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiArrowLeft, FiSave, FiTrash2, FiLoader } = FiIcons;

const QuoteAdmin = () => {
  const navigate = useNavigate();
  const { action, id } = useParams();
  const { isAdmin } = useAdmin();
  const { addNotification } = useApp();
  
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    topic_id: '',
    text: '',
    author: '',
    is_active: true
  });

  // Determine the form type based on the action
  const getFormType = () => {
    if (action === 'add' && id) return 'add-quote';
    if (action === 'edit' && id) return 'edit-quote';
    if (action === 'category/add') return 'add-category';
    if (action === 'topic/add' && id) return 'add-topic';
    return 'unknown';
  };
  
  const formType = getFormType();

  // Fetch necessary data based on form type
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // For all forms, fetch categories
        if (['add-quote', 'edit-quote', 'add-topic'].includes(formType)) {
          const categoriesData = await getQuoteCategories();
          setCategories(categoriesData);
        }
        
        // For quote forms, fetch topics
        if (['add-quote', 'edit-quote'].includes(formType)) {
          const topicsData = await getAllTopics();
          setTopics(topicsData);
          
          // For add-quote with topic ID, pre-select the topic
          if (formType === 'add-quote') {
            setFormData(prev => ({ ...prev, topic_id: id }));
          }
        }
        
        // For edit-quote, fetch the quote data
        if (formType === 'edit-quote') {
          // This is a simplified approach; in a real app, you'd fetch the specific quote
          const topicQuotes = await getQuotesByTopic(id);
          const quote = topicQuotes.find(q => q.id === id);
          if (quote) {
            setFormData({
              text: quote.text,
              author: quote.author,
              topic_id: quote.topic_id,
              is_active: quote.is_active
            });
          }
        }
        
        // For add-topic with category ID, pre-select the category
        if (formType === 'add-topic') {
          setFormData(prev => ({ ...prev, category_id: id }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load data. Please try again.'
        });
      } finally {
        setLoading(false);
      }
    };

    // Only proceed if user is admin
    if (isAdmin) {
      fetchData();
    } else {
      addNotification({
        type: 'error',
        title: 'Access Denied',
        message: 'You do not have permission to access this page.'
      });
      navigate('/quotes');
    }
  }, [formType, id, isAdmin, addNotification, navigate]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      let result;
      
      switch (formType) {
        case 'add-quote':
          result = await createQuote({
            text: formData.text,
            author: formData.author,
            topic_id: formData.topic_id,
            is_active: formData.is_active
          });
          addNotification({
            type: 'success',
            title: 'Quote Added',
            message: 'New quote has been added successfully!'
          });
          navigate(`/quotes/${result.topic_id}`);
          break;
          
        case 'edit-quote':
          result = await updateQuote(id, {
            text: formData.text,
            author: formData.author,
            topic_id: formData.topic_id,
            is_active: formData.is_active
          });
          addNotification({
            type: 'success',
            title: 'Quote Updated',
            message: 'Quote has been updated successfully!'
          });
          navigate(`/quotes/${result.topic_id}`);
          break;
          
        case 'add-category':
          result = await createCategory({
            name: formData.name,
            description: formData.description
          });
          addNotification({
            type: 'success',
            title: 'Category Added',
            message: 'New category has been added successfully!'
          });
          navigate(`/quotes/${result.id}`);
          break;
          
        case 'add-topic':
          result = await createTopic({
            name: formData.name,
            description: formData.description,
            category_id: formData.category_id
          });
          addNotification({
            type: 'success',
            title: 'Topic Added',
            message: 'New topic has been added successfully!'
          });
          navigate(`/quotes/${formData.category_id}/${result.id}`);
          break;
          
        default:
          throw new Error('Unknown form type');
      }
    } catch (error) {
      console.error('Error saving data:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to save. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete quote
  const handleDelete = async () => {
    if (formType !== 'edit-quote' || !id) return;
    
    if (window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
      setSaving(true);
      try {
        await deleteQuote(id);
        addNotification({
          type: 'success',
          title: 'Quote Deleted',
          message: 'Quote has been deleted successfully!'
        });
        navigate(`/quotes/${formData.topic_id}`);
      } catch (error) {
        console.error('Error deleting quote:', error);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete quote. Please try again.'
        });
      } finally {
        setSaving(false);
      }
    }
  };

  const renderPageTitle = () => {
    switch (formType) {
      case 'add-quote': return 'Add New Quote';
      case 'edit-quote': return 'Edit Quote';
      case 'add-category': return 'Add New Category';
      case 'add-topic': return 'Add New Topic';
      default: return 'Quote Management';
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16 px-4">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="flex items-center mb-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 mr-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{renderPageTitle()}</h1>
          </div>
        </div>
      </motion.div>

      {/* Form */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Quote Form */}
          {['add-quote', 'edit-quote'].includes(formType) && (
            <>
              <div>
                <label htmlFor="topic_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Topic
                </label>
                <select
                  id="topic_id"
                  name="topic_id"
                  value={formData.topic_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select a Topic</option>
                  {topics.map(topic => (
                    <option key={topic.id} value={topic.id}>
                      {topic.name} ({topic.category?.name})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-1">
                  Quote Text
                </label>
                <textarea
                  id="text"
                  name="text"
                  value={formData.text}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter the quote text"
                  required
                ></textarea>
              </div>

              <div>
                <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
                  Author
                </label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter the author's name"
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active (visible to users)
                </label>
              </div>
            </>
          )}

          {/* Category Form */}
          {formType === 'add-category' && (
            <>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter a brief description of this category"
                ></textarea>
              </div>
            </>
          )}

          {/* Topic Form */}
          {formType === 'add-topic' && (
            <>
              <div>
                <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select a Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Topic Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter topic name"
                  required
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter a brief description of this topic"
                ></textarea>
              </div>
            </>
          )}

          {/* Form Actions */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            {formType === 'edit-quote' && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="flex items-center space-x-2 text-red-600 hover:text-red-700 transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                <span>Delete</span>
              </button>
            )}

            <button
              type="submit"
              disabled={saving}
              className="flex items-center space-x-2 bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors ml-auto"
            >
              {saving ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="w-5 h-5" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default QuoteAdmin;